import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const AuthCallback = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setUser, setIsAuthenticated } = useAuth();
    const hasProcessed = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Use ref to prevent double processing in StrictMode
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const processAuth = async () => {
            // Extract session_id from URL hash
            const hash = location.hash;
            console.log('Auth callback - processing hash:', hash);
            
            const params = new URLSearchParams(hash.replace('#', ''));
            const sessionId = params.get('session_id');

            if (!sessionId) {
                console.error('No session_id found in URL hash');
                setError('No session ID found. Please try logging in again.');
                return;
            }

            try {
                console.log('Exchanging session_id for user data...');
                const response = await authAPI.exchangeSession(sessionId);
                const userData = response.data;
                
                console.log('Session exchange successful, user:', userData.email);
                
                setUser(userData);
                setIsAuthenticated(true);

                // Clear the hash from URL before redirecting
                window.history.replaceState(null, '', window.location.pathname);

                // Redirect based on terms acceptance and first login status
                if (!userData.terms_accepted) {
                    navigate('/terms/accept', { replace: true });
                } else {
                    navigate('/welcome', { replace: true });
                }
            } catch (error) {
                console.error('Auth exchange error:', error);
                // Clear the hash to prevent retry loops on page refresh
                window.history.replaceState(null, '', window.location.pathname);
                const errorMessage = error.response?.data?.detail || error.message || 'Authentication failed. Please try again.';
                setError(errorMessage);
            }
        };

        processAuth();
    }, [location.hash, navigate, setUser, setIsAuthenticated]);

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="auth-error">
                <Card className="max-w-md w-full">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                            <AlertCircle className="h-8 w-8 text-destructive" />
                        </div>
                        <CardTitle>Authentication Failed</CardTitle>
                        <CardDescription>{error}</CardDescription>
                    </CardHeader>
                    <CardContent className="text-center space-y-3">
                        <Button onClick={() => {
                            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                            const redirectUrl = window.location.origin + '/welcome';
                            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                        }}>
                            Try Again
                        </Button>
                        <Button variant="outline" onClick={() => navigate('/')}>
                            Go Home
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-callback">
            <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Signing you in...</p>
            </div>
        </div>
    );
};

export default AuthCallback;
